import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, Clock, Upload, Calendar, Loader2, XCircle } from 'lucide-react';
import { api } from '@/api';
import { toast } from 'sonner';

// Logo URL
const LOGO_URL = '/gantt-logo-blue.png';

export default function DelayActionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const action = searchParams.get('action');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [actionDetails, setActionDetails] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [reason, setReason] = useState('');
  const [ceaseDate, setCeaseDate] = useState('');
  const [attachments, setAttachments] = useState([]);
  
  useEffect(() => {
    if (token) {
      loadActionDetails();
    } else {
      setError('Invalid or missing token');
      setLoading(false);
    }
  }, [token]);
  
  const loadActionDetails = async () => {
    try {
      const response = await api.delayMonitoring.getActionDetails(token);
      setActionDetails(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
      setLoading(false);
    }
  };
  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    // In a real implementation, upload files to storage
    // For now, just store file names
    const newAttachments = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file_name: file.name,
      file_type: file.type.includes('image') ? 'photo' : 'other',
      file_url: URL.createObjectURL(file)
    }));
    setAttachments([...attachments, ...newAttachments]);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    if (action === 'ceased' && !ceaseDate) {
      toast.error('Please select a cease date');
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (action === 'not_ceased') {
        // Submit weekly update
        await api.delayMonitoring.addWeeklyUpdate(actionDetails.event_id, {
          update_type: 'not_ceased',
          reason: reason,
          attachments: attachments
        });
        toast.success('Status updated successfully');
      } else if (action === 'ceased') {
        // Submit cease request
        await api.delayMonitoring.submitCease(actionDetails.event_id, {
          cease_date: new Date(ceaseDate).toISOString(),
          cease_reason: reason,
          attachments: attachments
        });
        toast.success('Cease submitted successfully. PM will be notified for confirmation.');
      }
      
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f4f7fa' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#f4f7fa' }}>
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Invalid or Expired</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#f4f7fa' }}>
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {action === 'ceased' ? 'Cease Submitted' : 'Status Updated'}
            </h2>
            <p className="text-slate-600 mb-6">
              {action === 'ceased' 
                ? 'Your cease submission has been sent to the Project Manager for confirmation.'
                : 'The delay status has been updated. You will receive another reminder next week.'}
            </p>
            <Button onClick={() => navigate('/login')}>
              Go to Application
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#f4f7fa' }}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pt-8">
          <img src={LOGO_URL} alt="Gantt" className="h-20 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Delay Status Update</h1>
          <p className="text-slate-600">
            {action === 'not_ceased' 
              ? 'Please confirm the delay is still ongoing and provide details.'
              : 'Please provide details about when and why the delay ceased.'}
          </p>
        </div>
        
        {/* Event Info Card */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Delay Event Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Project</p>
                <p className="font-medium">{actionDetails?.project_name}</p>
              </div>
              <div>
                <p className="text-slate-500">Event Type</p>
                <p className="font-medium">{actionDetails?.event_type}</p>
              </div>
              <div>
                <p className="text-slate-500">Start Date</p>
                <p className="font-medium">
                  {actionDetails?.start_date 
                    ? new Date(actionDetails.start_date).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Days Ongoing</p>
                <p className={`font-bold ${
                  actionDetails?.days_ongoing >= 30 ? 'text-red-600' :
                  actionDetails?.days_ongoing >= 14 ? 'text-orange-600' :
                  'text-amber-600'
                }`}>
                  {actionDetails?.days_ongoing} days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Action Form */}
        <Card>
          <CardHeader className={action === 'ceased' ? 'bg-green-50 border-b' : 'bg-amber-50 border-b'}>
            <CardTitle className="text-lg flex items-center gap-2">
              {action === 'ceased' ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-green-800">Delay Has Ceased</span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <span className="text-amber-800">Delay Has Not Yet Ceased</span>
                </>
              )}
            </CardTitle>
            <CardDescription>
              {action === 'ceased'
                ? 'Provide the cease date, reason, and supporting evidence.'
                : 'Explain why the delay is still ongoing and any updates.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Cease Date (only for ceased action) */}
              {action === 'ceased' && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Cease Date <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="date"
                      value={ceaseDate}
                      onChange={(e) => setCeaseDate(e.target.value)}
                      className="pl-10"
                      required
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
              )}
              
              {/* Reason */}
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  {action === 'ceased' ? 'Reason for Ceasing' : 'Reason Delay Continues'} <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={action === 'ceased' 
                    ? 'Explain what resolved the delay (e.g., materials arrived, instruction received, weather cleared)...'
                    : 'Explain why the delay is still ongoing (e.g., still waiting for materials, weather conditions persist)...'}
                  rows={4}
                  className="mt-1"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Minimum 20 characters required</p>
              </div>
              
              {/* Attachments */}
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  Supporting Evidence {action === 'ceased' && <span className="text-slate-400">(Recommended)</span>}
                </Label>
                <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors">
                  <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 mb-2">
                    Upload photos, emails, or documents
                  </p>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <label htmlFor="file-upload">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>Select Files</span>
                    </Button>
                  </label>
                </div>
                
                {/* Uploaded Files List */}
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                        <span className="text-sm text-slate-700 truncate">{file.file_name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setAttachments(attachments.filter((_, i) => i !== index))}
                        >
                          <XCircle className="h-4 w-4 text-slate-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className={`w-full ${
                    action === 'ceased' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                  disabled={submitting || reason.length < 20}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    action === 'ceased' ? 'Submit Cease Request' : 'Confirm Delay Ongoing'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-xs text-slate-500 mt-8">
          This link is valid for 7 days. If you need assistance, please contact your project administrator.
        </p>
      </div>
    </div>
  );
}
